/**
 * Reading Hub - Scan & Read Library
 * 
 * Feb 2026 Redesign:
 * - Removed Storybooks tab (archived)
 * - Added search bar
 * - Added view toggle (grid/list)
 * 
 * Design: Apple-inspired dark glassmorphism
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  BookPlus,
  Clock, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  User,
  Search,
  LayoutGrid,
  List,
  X,
  Trash2,
  XCircle,
  Lightbulb,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { useViewAs } from '@/contexts/ViewAsContext';
import { useSelectedChild } from '@/contexts/ChildContext';
import { useParentStatus } from '@/hooks/useParentStatus';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { AddBookFlow } from '@/components/library/AddBookFlow';

// ============================================================================
// Types
// ============================================================================

interface ScannedBook {
  id: string;
  title: string;
  author: string | null;
  cover_image_url: string | null;
  quality_status: string | null;
  turn_art_status: string | null;
  content_source: string | null;
  created_at: string | null;
}

interface BookRequest {
  id: string;
  title: string;
  status: string;
  cover_image_url: string | null;
  created_at: string | null;
  estimated_ready_date: string | null;
}

type ViewMode = 'grid' | 'list';

// ============================================================================
// Main Component
// ============================================================================

export const ReadingHub: React.FC = () => {
  const { profile } = useAuth();
  const { effectiveParentId } = useViewAs();
  const { selectedChildId, children, isMultiChild, setSelectedChildId } = useSelectedChild();
  const parentStatus = useParentStatus({ childId: selectedChildId ?? undefined });
  const navigate = useNavigate();

  // Data state
  const [books, setBooks] = useState<ScannedBook[]>([]);
  const [pendingRequests, setPendingRequests] = useState<BookRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // UI state
  const [showAddBook, setShowAddBook] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [deletingBookId, setDeletingBookId] = useState<string | null>(null);
  const [confirmDeleteBook, setConfirmDeleteBook] = useState<ScannedBook | null>(null);
  const [confirmCancelRequest, setConfirmCancelRequest] = useState<BookRequest | null>(null);
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);
  
  // Filter books by search query
  const filteredBooks = useMemo(() => {
    if (!searchQuery.trim()) return books;
    const query = searchQuery.toLowerCase();
    return books.filter(book => 
      book.title.toLowerCase().includes(query) ||
      (book.author && book.author.toLowerCase().includes(query))
    );
  }, [books, searchQuery]);

  // Use effectiveParentId for data queries (supports admin preview via ?viewAs=)
  const parentId = effectiveParentId || profile?.id;

  // Load data - extracted for reuse (M-14: replaces window.location.reload)
  const loadData = useCallback(async () => {
    if (!parentId) return;

    setLoading(true);
    setLoadError(null);

    try {
      // Get ALL books from parent's library (full set)
      const { data: libraryData } = await supabase
        .from('parent_book_library')
        .select(`
          scanned_books (
            id,
            title,
            author,
            cover_image_url,
            quality_status,
            turn_art_status,
            content_source,
            created_at
          )
        `)
        .eq('parent_id', parentId)
        .order('added_at', { ascending: false });
      
      let booksList: ScannedBook[] = [];
      if (libraryData) {
        booksList = libraryData
          .map(item => item.scanned_books)
          .filter((book): book is NonNullable<typeof book> => book !== null)
          .map(book => ({
            id: book.id,
            title: book.title,
            author: book.author,
            cover_image_url: book.cover_image_url,
            quality_status: book.quality_status,
            turn_art_status: book.turn_art_status,
            content_source: (book as any).content_source ?? null,
            created_at: book.created_at,
          }));
      }

      // Determine which child to filter by (from ChildContext)
      const activeChildId = selectedChildId;

      if (activeChildId) {
        // Check if this child has any book assignments
        const { data: childAssignments } = await supabase
          .from('child_book_library')
          .select('book_id')
          .eq('child_id', activeChildId);

        if (childAssignments && childAssignments.length > 0) {
          // Child has assignments: filter to only their books
          const assignedBookIds = new Set(childAssignments.map(a => a.book_id));
          booksList = booksList.filter(b => assignedBookIds.has(b.id));
        }
        // If no assignments yet, show all parent books (graceful fallback)
      }

      setBooks(booksList);
      
      // Get pending book requests
      const { data: requestsData } = await supabase
        .from('book_scan_requests')
        .select('id, title, status, cover_image_url, created_at, estimated_ready_date')
        .eq('requested_by', parentId)
        .in('status', ['pending', 'scanning', 'processing'])
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      
      if (requestsData) {
        setPendingRequests(requestsData as unknown as BookRequest[]);
      }
    } catch (err) {
      console.error('Error loading books:', err);
      setLoadError('Failed to load your library. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [parentId, selectedChildId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!parentStatus.isLoading && !parentStatus.canAccessLearn) {
    navigate('/');
    return null;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <span className="flex items-center gap-1 text-xs text-[#34C759]">
            <CheckCircle className="w-3 h-3" />
            Ready
          </span>
        );
      case 'pending':
      case 'scanning':
      case 'processing':
        return (
          <span className="flex items-center gap-1 text-xs text-[#FF9500]">
            <Clock className="w-3 h-3" />
            Processing
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-xs text-[#8E8E93]">
            <AlertCircle className="w-3 h-3" />
            {status}
          </span>
        );
    }
  };

  // Delete book from library (removes from parent_book_library AND child assignments)
  const handleDeleteBook = async (book: ScannedBook) => {
    if (!parentId) return;
    
    setDeletingBookId(book.id);
    
    try {
      // Remove from parent library
      const { error } = await supabase
        .from('parent_book_library')
        .delete()
        .eq('parent_id', parentId)
        .eq('book_id', book.id);
      
      if (error) throw error;

      // Also remove child assignments for this parent's children
      const childIds = children.map(c => c.id);
      if (childIds.length > 0) {
        await supabase
          .from('child_book_library')
          .delete()
          .eq('book_id', book.id)
          .in('child_id', childIds);
      }
      
      // Remove from local state
      setBooks(prev => prev.filter(b => b.id !== book.id));
      setConfirmDeleteBook(null);
    } catch (err) {
      console.error('Error removing book from library:', err);
    } finally {
      setDeletingBookId(null);
    }
  };

  const handleCancelRequest = async (request: BookRequest) => {
    if (!parentId) return;
    setCancellingRequestId(request.id);
    try {
      const { error } = await supabase
        .from('book_scan_requests')
        .update({ status: 'cancelled' })
        .eq('id', request.id)
        .eq('requested_by', parentId);

      if (error) throw error;

      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
      setConfirmCancelRequest(null);
      toast.success('Book request cancelled');

      // Send cancellation confirmation email
      try {
        await supabase.functions.invoke('book-notifications', {
          body: { action: 'send-request-cancelled', requestId: request.id },
        });
      } catch {
        // Email is best-effort
      }
    } catch (err) {
      console.error('Error cancelling request:', err);
      toast.error('Failed to cancel request');
    } finally {
      setCancellingRequestId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#1A1A1A] mx-auto mb-3" />
          <p className="text-[#636366]">Loading your library...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-[#FF3B30]" />
          </div>
          <h2 className="text-lg font-semibold text-[#1C1C1E] mb-2">Something went wrong</h2>
          <p className="text-[#636366] mb-6 max-w-sm mx-auto">{loadError}</p>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#111111] hover:brightness-110 text-[#1A1A1A] rounded-xl font-medium transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Add Book Modal */}
      {showAddBook && (
        <AddBookFlow
          onClose={() => setShowAddBook(false)}
          onBookAdded={(bookId) => {
            console.log('Book added:', bookId);
            setShowAddBook(false);
            loadData();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteBook && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm rounded-2xl p-6"
            style={glassStyles.card}
          >
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FF3B30]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-7 h-7 text-[#FF3B30]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">Remove Book?</h3>
              <p className="text-[#636366] text-sm mb-6">
                Remove <span className="text-[#1C1C1E] font-medium">"{confirmDeleteBook.title}"</span> from your library? 
                You can add it back later.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDeleteBook(null)}
                  className="flex-1 py-2.5 px-4 bg-black/[0.05] hover:bg-black/[0.06] text-[#1C1C1E] rounded-xl font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteBook(confirmDeleteBook)}
                  disabled={deletingBookId === confirmDeleteBook.id}
                  className="flex-1 py-2.5 px-4 bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-[#1C1C1E] rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingBookId === confirmDeleteBook.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cancel Request Confirmation Modal */}
      {confirmCancelRequest && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm rounded-2xl p-6"
            style={glassStyles.card}
          >
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FF9500]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-7 h-7 text-[#FF9500]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">Cancel Request?</h3>
              <p className="text-[#636366] text-sm mb-6">
                Cancel your request for <span className="text-[#1C1C1E] font-medium">"{confirmCancelRequest.title}"</span>? 
                You'll receive a confirmation email.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmCancelRequest(null)}
                  className="flex-1 py-2.5 px-4 bg-black/[0.05] hover:bg-black/[0.06] text-[#1C1C1E] rounded-xl font-medium transition-all"
                >
                  Keep
                </button>
                <button
                  onClick={() => handleCancelRequest(confirmCancelRequest)}
                  disabled={cancellingRequestId === confirmCancelRequest.id}
                  className="flex-1 py-2.5 px-4 bg-[#FF9500] hover:bg-[#FF9500]/80 text-black rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancellingRequestId === confirmCancelRequest.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Cancel Request'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header Section — sticky below AppLayout header (h-14 = 56px) */}
      <div className="sticky top-14 z-10 bg-white">
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-4">
        {/* Title Row */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF9500]/15 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#FF9500]" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[#1C1C1E]">Read</h1>
              <p className="text-sm text-[#8E8E93]">{books.length} book{books.length !== 1 ? 's' : ''} in library</p>
            </div>
          </div>
          
          {/* Request Book Button — prominent on mobile */}
          <button
            onClick={() => setShowAddBook(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#111111] text-[#1A1A1A] text-sm font-medium transition-all hover:brightness-110"
          >
            <BookPlus className="w-4 h-4" />
            Request Book
          </button>
        </div>
        
        {/* Search + View Toggle Row */}
        <div className="flex items-center gap-3 mb-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]" />
            <input
              type="text"
              placeholder="Search books..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:border-[#1A1A1A]/50 focus:ring-1 focus:ring-[#1A1A1A]/30 transition-all text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center bg-black/[0.03] rounded-xl p-1 border border-black/[0.08]">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-all ${
                viewMode === 'grid' 
                  ? 'bg-black/[0.05] text-[#1C1C1E]' 
                  : 'text-[#8E8E93] hover:text-[#636366]'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-all ${
                viewMode === 'list' 
                  ? 'bg-black/[0.05] text-[#1C1C1E]' 
                  : 'text-[#8E8E93] hover:text-[#636366]'
              }`}
              title="List view"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Child Selector - shown when multiple children */}
        {isMultiChild && (
          <div className="mb-4 p-3 rounded-xl bg-black/[0.03] border border-black/[0.08]">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-[#8E8E93]" />
              <span className="text-sm text-[#636366]">Reading as:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {children.map(child => (
                <button
                  key={child.id}
                  onClick={() => setSelectedChildId(child.id)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    selectedChildId === child.id
                      ? 'bg-[#111111] text-white'
                      : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.05] hover:text-[#1C1C1E]'
                  }`}
                >
                  {child.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 pb-6">
        {/* Requested Books - displayed as book cards in the main grid */}
        {/* (Integrated into grid below via requestedBookCards) */}

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-4 text-sm text-[#636366]">
            {filteredBooks.length === 0 ? (
              <span>No books found for "{searchQuery}"</span>
            ) : (
              <span>Found {filteredBooks.length} book{filteredBooks.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        )}

        {/* Books Display */}
        <AnimatePresence mode="wait">
          {(filteredBooks.length > 0 || pendingRequests.length > 0) ? (
            viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-2 sm:grid-cols-3 gap-4"
              >
                {/* Requested books shown first */}
                {pendingRequests.map(request => (
                  <div
                    key={`req-${request.id}`}
                    className="rounded-2xl overflow-hidden relative"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,159,10,0.1), rgba(255,159,10,0.05))',
                      border: '1px solid rgba(255,159,10,0.2)',
                    }}
                  >
                    {request.cover_image_url ? (
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img src={request.cover_image_url} alt={request.title || 'Book cover'} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/20 flex flex-col items-center justify-center">
                          <Clock className="w-6 h-6 text-[#FF9500] mb-1" />
                          <span className="text-xs text-[#FF9500] font-medium">Requested</span>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-[3/4] flex flex-col items-center justify-center p-4 text-center">
                        <Clock className="w-8 h-8 text-[#FF9500] mb-3" />
                        <p className="text-sm font-medium text-[#1C1C1E] leading-tight line-clamp-3">
                          {request.title}
                        </p>
                      </div>
                    )}
                    {/* Status badge + cancel */}
                    <div className="px-3 py-2 bg-black/20 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#FF9500] font-medium">Requested</p>
                        <p className="text-[10px] text-[#8E8E93]">
                          {request.estimated_ready_date
                            ? `Ready by ${new Date(request.estimated_ready_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                            : 'Processing'}
                        </p>
                      </div>
                      <button
                        onClick={() => setConfirmCancelRequest(request)}
                        className="p-1.5 rounded-lg bg-black/[0.03] hover:bg-black/[0.05] text-[#8E8E93] hover:text-[#FF3B30] transition-all"
                        title="Cancel request"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {filteredBooks.map((book, index) => (
                  <BookGridCard 
                    key={book.id} 
                    book={book} 
                    index={index}
                    selectedChildId={selectedChildId ?? undefined}
                    getStatusBadge={getStatusBadge}
                    onDelete={setConfirmDeleteBook}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-3"
              >
                {/* Requested books shown first in list view */}
                {pendingRequests.map(request => (
                  <div
                    key={`req-${request.id}`}
                    className="p-4 rounded-xl flex items-center justify-between"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,159,10,0.1), rgba(255,159,10,0.05))',
                      border: '1px solid rgba(255,159,10,0.2)',
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {request.cover_image_url ? (
                        <div className="w-10 h-14 rounded-lg overflow-hidden flex-shrink-0 relative">
                          <img src={request.cover_image_url} alt={request.title || 'Book cover'} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Clock className="w-4 h-4 text-[#FF9500]" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-10 h-14 bg-[#FF9500]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-[#FF9500]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-[#1C1C1E] truncate">{request.title}</p>
                        <p className="text-xs text-[#8E8E93]">
                          {request.estimated_ready_date
                            ? `Requested – ready by ${new Date(request.estimated_ready_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                            : 'Requested – processing'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs text-[#FF9500] bg-[#FF9500]/10 px-2 py-1 rounded-full">
                        {request.status === 'pending' ? 'In Queue' : 'Processing'}
                      </span>
                      <button
                        onClick={() => setConfirmCancelRequest(request)}
                        className="p-1.5 rounded-lg bg-black/[0.03] hover:bg-black/[0.05] text-[#8E8E93] hover:text-[#FF3B30] transition-all"
                        title="Cancel request"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {filteredBooks.map((book, index) => (
                  <BookListCard 
                    key={book.id} 
                    book={book} 
                    index={index}
                    selectedChildId={selectedChildId ?? undefined}
                    getStatusBadge={getStatusBadge}
                    onDelete={setConfirmDeleteBook}
                  />
                ))}
              </motion.div>
            )
          ) : (filteredBooks.length === 0 && books.length > 0 && searchQuery) ? (
            <motion.div
              key="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12"
            >
              <p className="text-[#636366]">No books found matching "{searchQuery}"</p>
            </motion.div>
          ) : (books.length === 0 && pendingRequests.length === 0) ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-16 rounded-2xl"
              style={glassStyles.card}
            >
              <div className="w-20 h-20 bg-[#FF9500]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-[#FF9500]" />
              </div>
              <h2 className="text-xl font-semibold text-[#1C1C1E] mb-2">
                No books yet
              </h2>
              <p className="text-[#636366] max-w-md mx-auto mb-6">
                Scan a book's ISBN barcode to request it for your library.
                Our team will process it within 72 hours.
              </p>
              <button
                onClick={() => setShowAddBook(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#111111] hover:brightness-110 text-[#1A1A1A] rounded-xl font-medium transition-all"
              >
                <BookPlus className="w-5 h-5" />
                Request Your First Book
              </button>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Help Text */}
        {books.length > 0 && (
          <div className="mt-8 p-4 rounded-xl bg-black/[0.03] border border-black/[0.08]">
            <h3 className="font-medium text-[#1C1C1E] mb-2">How it works</h3>
            <ol className="space-y-2 text-sm text-[#636366]">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#111111]/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-[#1A1A1A] font-medium">1</span>
                <span>Tap "Request Book" and scan the ISBN barcode on any children's book</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#111111]/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-[#1A1A1A] font-medium">2</span>
                <span>Our team processes the book and adds Turn Color phonics support</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#111111]/20 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-[#1A1A1A] font-medium">3</span>
                <span>Read together with color-coded text that helps your child decode words</span>
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Book Grid Card Component
// ============================================================================

interface BookCardProps {
  book: ScannedBook;
  index: number;
  selectedChildId?: string;
  getStatusBadge: (status: string) => React.ReactNode;
  onDelete: (book: ScannedBook) => void;
}

const BookGridCard: React.FC<BookCardProps> = ({ 
  book, 
  index, 
  selectedChildId,
  getStatusBadge,
  onDelete,
}) => {
  const bookUrl = selectedChildId 
    ? `/read/book/${book.id}?child=${selectedChildId}` 
    : `/read/book/${book.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="group/card relative"
    >
      <Link to={bookUrl} className="block">
        <div 
          className="rounded-xl overflow-hidden transition-all group-hover/card:brightness-110"
          style={glassStyles.card}
        >
          {/* Cover */}
          <div className="aspect-[3/4] bg-black/[0.03] relative">
            {book.cover_image_url ? (
              <img
                src={book.cover_image_url}
                alt={book.title}
                className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-[#C7C7CC]" />
              </div>
            )}
            
            {/* Status Badge */}
            <div className="absolute top-2 right-2">
              {getStatusBadge(book.quality_status || 'unknown')}
            </div>
          </div>
          
          {/* Info */}
          <div className="p-3">
            <h3 className="font-medium text-[#1C1C1E] text-sm line-clamp-2 mb-1 group-hover/card:text-[#1A1A1A] transition-colors">
              {book.title}
            </h3>
            {book.author && (
              <p className="text-xs text-[#8E8E93] line-clamp-1">
                {book.author}
              </p>
            )}
            {book.content_source === 'generated' && (
              <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-[#0A84FF] bg-[#0A84FF]/10 px-2 py-0.5 rounded-full">
                <Lightbulb className="w-2.5 h-2.5" />
                Knowledge
              </span>
            )}
          </div>
        </div>
      </Link>
      
      {/* Delete Button - appears on hover */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete(book);
        }}
        className="absolute top-2 left-2 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg bg-black/25 backdrop-blur-sm text-[#636366] hover:text-[#FF3B30] hover:bg-black/30 opacity-100 sm:opacity-0 sm:group-hover/card:opacity-100 transition-all z-10"
        title="Remove from library"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// ============================================================================
// Book List Card Component
// ============================================================================

const BookListCard: React.FC<BookCardProps> = ({ 
  book, 
  index, 
  selectedChildId,
  getStatusBadge,
  onDelete,
}) => {
  const bookUrl = selectedChildId 
    ? `/read/book/${book.id}?child=${selectedChildId}` 
    : `/read/book/${book.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, x: -15 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="group/card"
    >
      <div 
        className="flex items-center gap-4 p-3 rounded-xl transition-all hover:bg-black/[0.03]"
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        {/* Cover Thumbnail - clickable */}
        <Link to={bookUrl} className="w-16 h-20 rounded-lg overflow-hidden bg-black/[0.03] flex-shrink-0">
          {book.cover_image_url ? (
            <img
              src={book.cover_image_url}
              alt={book.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#C7C7CC]" />
            </div>
          )}
        </Link>
        
        {/* Info - clickable */}
        <Link to={bookUrl} className="flex-1 min-w-0">
          <h3 className="font-medium text-[#1C1C1E] text-sm line-clamp-1 group-hover/card:text-[#1A1A1A] transition-colors">
            {book.title}
          </h3>
          {book.author && (
            <p className="text-xs text-[#8E8E93] line-clamp-1 mt-0.5">
              {book.author}
            </p>
          )}
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            {getStatusBadge(book.quality_status || 'unknown')}
            {book.content_source === 'generated' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0A84FF] bg-[#0A84FF]/10 px-2 py-0.5 rounded-full">
                <Lightbulb className="w-2.5 h-2.5" />
                Knowledge
              </span>
            )}
          </div>
        </Link>
        
        {/* Delete Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(book);
          }}
          className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-[#C7C7CC] hover:text-[#FF3B30] hover:bg-black/[0.03] opacity-100 sm:opacity-0 sm:group-hover/card:opacity-100 transition-all flex-shrink-0"
          title="Remove from library"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default ReadingHub;
